import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  useLiveEventsContext,
  type LiveEventHandler,
  type LiveEventsContextValue,
} from '../contexts/LiveEventsContext';

/** `{status, subscribe}` of the app-wide live event stream. */
export function useLiveEvents(): LiveEventsContextValue {
  return useLiveEventsContext();
}

/**
 * Subscribe to one event type for the lifetime of the component. The handler
 * may be an inline closure: the latest one is always called, without
 * re-subscribing on every render.
 *
 * Layout effects on purpose: the subscription and the latest handler are in
 * place when the render commits, so an event that arrives before the passive
 * effects ran is neither lost nor handled by the previous render's closure.
 */
export function useLiveEvent<T = any>(type: string, handler: LiveEventHandler<T>): void {
  const { subscribe } = useLiveEvents();
  const latest = useRef(handler);

  useLayoutEffect(() => {
    latest.current = handler;
  });

  useLayoutEffect(
    () => subscribe<T>(type, (data, event) => latest.current(data, event)),
    [subscribe, type],
  );
}

export const DEGRADED_POLL_INTERVAL_MS = 5_000;

/**
 * Contract §1: after two failed reconnects the stream reports `degraded` and
 * the page falls back to polling every 5 s until the stream is `open` again.
 * `poll` is called on that interval only while degraded, and never while the
 * tab is hidden (nobody looks, and every open tab would hit the API every
 * 5 s); coming back to the tab polls once right away.
 */
export function useDegradedPolling(poll: () => void, intervalMs = DEGRADED_POLL_INTERVAL_MS): void {
  const { status } = useLiveEvents();
  const latest = useRef(poll);

  useEffect(() => {
    latest.current = poll;
  });

  useEffect(() => {
    if (status !== 'degraded') return undefined;
    const tick = () => {
      if (!document.hidden) latest.current();
    };
    const onVisibilityChange = () => {
      if (!document.hidden) latest.current();
    };
    const id = setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [status, intervalMs]);
}

export default useLiveEvents;
